import def from "csi/def";
import RendererConstructor, { Renderer } from "csi/heading/Renderer";
import StringRen from "csi/heading/String";
import stateColorResolver from "csi/heading/stateColorResolver";
import locale from "csi/locale";
import Table, { TableWidget } from "csi/table/Widget";
import types from "csi/types";
import PillAnchor from "csi/widget/PillAnchor";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import _ from "lodash";
import { C8Node } from "csi/typings";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import Spec, { QuerySpec } from "csi/core/Spec";
import MessageDialog from "csi/widget/MessageDialog";
import TextWithMaterialIcon from "csi/widget/TextWithMaterialIcon";
import comm from "csi/comm";
import html from "csi/html";
import { formatFileSize } from "csi/utility/fileUtility";
import domConstruct from "dojo/dom-construct";

const TYPE_REVISION = "DGaaSTemplateRevision";

function pill(revision: C8Node, _current: C8Node, refNode: HTMLElement): void {
	const name = locale.getNodeName(revision);
	const params: any = {
		href: def.getNodeBrowseURL(revision),
	};
	const state = revision.State;
	if (state === "Revision.State:DRAFT") {
		params["class"] = stateColorResolver.getStateCssByIndex(TYPE_REVISION, "DRAFT");
		params.value = locale.resource("Draft");
	} else if (state === "Revision.State:APPROVED") {
		params["class"] = stateColorResolver.getStateCssByIndex(TYPE_REVISION, "APPROVED");
		params.value = locale.resource("DGaaSTemplateRevisionLastPublished", [name]);
	} else {
		params["class"] = "csi-name-value-pair-transparent";
		params.value = locale.resource("DGaaSTemplateRevisionName", [name]);
	}
	new PillAnchor(params).placeAt(refNode);
}

/**
 * Renders the filename, progress bar, and status message with a detailed status message in case of an error while generating the document.
 * @param revision The revision of the file.
 * @param ctx The context object.
 * @param _h The heading element.
 * @param refNode The reference HTML element.
 * @param renderer The renderer object responsible for rendering.
 */
function renderFile(revision: C8Node, ctx: Context, _h: Heading, refNode: HTMLElement, renderer: Renderer): void {
	const fileCNL = revision?.File;
	const filename = revision?.["File.File"];
	const status = revision?.["File.Status"];
	const statusMessage = revision?.StatusMessage;
	const detailedStatusMessage = revision?.DetailedStatusMessage;

	const params: Record<string, string> = {
		href: comm.getFileHref(fileCNL, false, filename),
	};

	if (status) {
		// In this code, we're focusing on the DGaaSTemplate node as our context, aiming to monitor the File.Status attribute on the DGaaSTemplateRevision.
		// By setting the latestRevision URL here, we ensure that the monitor (urlMonitor: ctx.getNodeURL()) will continuously
		// monitoring the node for any changes to the attribute value and replacing the progress bar with the corresponding filename.
		const node = ctx.getNode();
		if (node.$URL === node.Master) {
			ctx.pushParams().setNodeURL(node.LatestRevision);
		}
		renderer._toHtml(ctx, _h, refNode, null, null);
		if (node.$URL === node.Master) {
			ctx.pop();
		}
	} else if (statusMessage) {
		new TextWithMaterialIcon({
			icon: "error",
			text: statusMessage,
			class: "csi-file-state-error",
			onClick: (): void => {
				const dlg = new MessageDialog({
					destroyOnHide: false,
					buttons: [MessageDialog.BUTTON_CLOSE],
					message: detailedStatusMessage,
					materialIcon: MessageDialog.ICON_ERROR,
					title: locale.resource("DocumentGenerationFailed"),
				});
				dlg.show();
			},
		}).placeAt(refNode);
	} else if (params.href) {
		// Add filename with href link
		html.a({
			href: params.href,
			class: "csi-dgaas-grouped-file-names",
			onclick: html.stopPropagation,
			target: "_blank",
			innerText: filename,
		}, refNode);
		// If there is a formatted file size, add FileSize to refNode
		if (ctx.view?.showFileSizes) {
			const fileSize = ctx.cache.getNode(fileCNL)?.FileSize;
			if (fileSize) {
				const formattedFileSize = ` (${formatFileSize(fileSize)})`;
				domConstruct.place(html.create("span", {
					innerText: formattedFileSize,
					class: "csi-dgaas-grouped-file-names",
				}), refNode);
			}
		}
	}
};

const _NameRenderer = declare(StringRen, {
	headingToHtml: function (ctx: Context, _h: Heading) {
		if (ctx.getNode().$Type !== TYPE_REVISION) {
			this.inherited(arguments);
		}
	},
});

const _PrimaryTypeRenderer = declare(StringRen, {

	/**
	 * @override
	 * Return localized name for primary C8 type
	 */
	formatValue(value: string): string {
		return locale.type(value);
	},

	/**
	 * Use this renderer to get proper formatting
	 * @param {Context} ctx
	 * @param {Heading} h
	 * @param {Spec} spec
	 */
	getQuerySpec(ctx: Context, h: Heading, spec: Spec): QuerySpec {
		var qs = this.inherited<QuerySpec>(arguments);
		qs.ren = this;
		return qs;
	},
});

const _VersionRenderer = declare(RendererConstructor, {
	/**
	 * @override
	 * Renders the state of latest and current DGaaStemplateRevision
	 * When expanding the DGaaStemplate nodename, this function displays revision state for each revision.
	 */
	toHtmlStatic(ctx: Context, _h: Heading, refNode: HTMLElement): boolean {
		const cache = ctx.cache;
		const node = ctx.getNode();
		const master = cache.get(node.Master);
		const latest = cache.get(master.LatestRevision);
		const current = cache.get(master.CurrentRevision);
		if (node === master) {
			// render pills for draft and/or last published revisions
			if (latest !== current) {
				pill(latest, current, refNode);
			}
			pill(current, current, refNode);
		} else {
			// render pill for current revision
			pill(node, current, refNode);
		}
		return true;
	},
});

const _FileRenderer = declare(RendererConstructor, {
	// Adding status attribute to monitor progressbar for preview
	idStatus: "File.Status",
	/**
	 * @override
	 * Renders the filename for the latest and current DGaaStemplateRevision of the node.
	 * When expanding the DGaaStemplate nodename, this function displays all filenames of each revision.
	 */
	toHtmlStatic(ctx: Context, _h: Heading, refNode: HTMLElement): boolean {
		const cache = ctx.cache;
		const node = ctx.getNode();
		const master = cache.get(node.Master);
		const latest = cache.get(master.LatestRevision);
		const current = cache.get(master.CurrentRevision);

		if (node === master) {
			if (latest !== current) {
				renderFile(latest, ctx, _h, refNode, this);
			}
			renderFile(current, ctx, _h, refNode, this);
		} else {
			renderFile(node, ctx, _h, refNode, this);
		}
		return true;
	},
});

const DGaaSTemplateTable = declare(Table, {
	class: "csi-table-align-middle csi-table-merge-hierarchy",
	selectWithHierarchy: true,
	/**
	 * @override
	 * Add headings to view
	 */
	postMixInProperties(): void {
		const table = this as unknown as TableWidget;
		const defView = table.defView;

		function newHeading(params: any): any {
			return _.extend(defView.newHeading(), params);
		}

		defView.m_headings = [
			newHeading({
				m_id: xml.ATTR_NAME,
				classRenderer: _NameRenderer,
			}),
			newHeading({
				m_id: "PrimaryType",
				classRenderer: _PrimaryTypeRenderer,
			}),
			newHeading({
				m_id: "PrimarySubtype",
			}),
			newHeading({
				m_id: "Active",
			}),
			newHeading({
				m_name: "Version",
				classRenderer: _VersionRenderer,
				columnWidth: types.MEDIUM,
			}),
			newHeading({
				m_name: "File",
				classRenderer: _FileRenderer,
				columnWidth: types.MEDIUM,
			}),
		];
		this.inherited(arguments);
	},
});
export = DGaaSTemplateTable;

